/**
 * 「もっと見る」（snippets/load-more.liquid）。次のページへのリンクを横取りして、そのページの商品を
 * 一覧（[data-load-more-list]）の末尾へ足す。JS が無い・失敗したときは、リンクがそのまま次のページへ遷移する。
 *
 * 足すのは次のページの一覧の子要素（カードと、最終行を閉じる空セル）。取り込む前に、いまの一覧の空セルは外す
 * （ページの区切りで偶数・3 の倍数の点数が続くので、足したあとの空セルは最後のページの分だけで合う）。
 * 描画は DOMParser で解析した別文書から importNode するだけで、innerHTML は使わない。
 * 取得は Shopify の Section Rendering（?section_id=）で、一覧のセクションだけを受け取る。取れなければページ全体を取る。
 *
 * 商品を開いて「戻る」で一覧に帰ってきたとき、ブラウザは最初の 24 点しか復元しない。そこで、何ページ分足したかと
 * スクロール位置を、この履歴エントリの history.state に持ち、戻ってきたら足し直して位置を戻す
 * （足したページは URL に出ない。?page=N にすると「N ページ目の 24 点だけ」の一覧になってしまうため）。
 * 再読み込みでは復元しない（state を捨てる）。足し直しのページは並べて取得する。
 */
(() => {
  const LABEL = 'もっと見る';
  const STATE_KEY = 'loadMore';

  const readState = () => (window.history.state && window.history.state[STATE_KEY]) || null;
  const writeState = (value) => {
    try {
      window.history.replaceState({ ...(window.history.state || {}), [STATE_KEY]: value }, '');
    } catch (err) {
      // 履歴に書けない環境では、戻ったとき最初の 24 点に戻るだけ
    }
  };

  // snippets/load-more.liquid と同じ書き方: 1 ページ目から「24 / 425」、途中のページからは「49〜72 / 425」
  const format = (start, end, total) => `${start === 1 ? end : `${start}〜${end}`} / ${total} 点を表示中`;

  const withParam = (href, key, value) => {
    const url = new URL(href, window.location.href);
    url.searchParams.set(key, value);
    return url.toString();
  };

  const parse = async (res) => new DOMParser().parseFromString(await res.text(), 'text/html');

  const bind = (nav) => {
    const list = document.querySelector('[data-load-more-list]');
    const count = nav.querySelector('[data-load-more-count]');
    if (!list) return;
    const sectionId = nav.dataset.sectionId;
    const start = Number(nav.dataset.start) || 1;
    const total = Number(nav.dataset.total) || 0;
    let end = Number(nav.dataset.end) || 0;
    let busy = false;
    let pages = 0;
    // 戻ってきて足し直しているあいだは、位置を上書きしない（足し直しが終わるまで scrollY は途中の値）
    let restoreY = null;

    // 1 ページぶんを取る: { children, nextHref, size }。Section Rendering で取れなければページ全体で取り直す
    const fetchPage = async (href) => {
      const get = async (url) => {
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(`load-more failed: ${res.status}`);
        return parse(res);
      };
      let doc = null;
      if (sectionId) {
        try {
          doc = await get(withParam(href, 'section_id', sectionId));
        } catch (err) {
          doc = null;
        }
      }
      let nextList = doc && doc.querySelector('[data-load-more-list]');
      if (!nextList) {
        doc = await get(href);
        nextList = doc.querySelector('[data-load-more-list]');
      }
      if (!nextList || nextList.children.length === 0) throw new Error('load-more: no list');
      const nextLink = doc.querySelector('[data-load-more] .load-more__next');
      // 件数は、空セルを除いた子（カードと、検索結果の商品以外の行）で数える（サーバー側の件数と同じ数え方）
      const items = [...nextList.children].filter((n) => !n.classList.contains('product-card--filler'));
      return { children: [...nextList.children], nextHref: nextLink ? nextLink.href : null, size: items.length };
    };

    // 取った 1 ページぶんを一覧の末尾へ足し、ボタンと件数を更新する。足した先頭のカードを返す
    const appendPage = (page, link) => {
      list.querySelectorAll('.product-card--filler').forEach((n) => n.remove());
      const added = page.children.map((n) => document.importNode(n, true));
      list.append(...added);
      end += page.size;
      if (count) count.textContent = format(start, Math.min(end, total), total);
      if (page.nextHref) {
        link.href = page.nextHref;
        link.removeAttribute('aria-disabled');
        link.textContent = LABEL;
      } else {
        link.remove();
      }
      pages += 1;
      writeState({ pages, y: restoreY === null ? window.scrollY : restoreY });
      return added.find((n) => n.matches && n.matches('a.product-card'));
    };

    nav.addEventListener('click', async (e) => {
      const link = e.target.closest('.load-more__next');
      if (!link) return;
      // 新しいタブで開くなどの操作は、ふつうのリンクのままにする
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      // 読み込みに失敗したあとの押下は、ふつうのリンクとして次のページへ遷移させる
      if (link.dataset.failed) return;
      e.preventDefault();
      if (busy) return;
      busy = true;
      link.setAttribute('aria-disabled', 'true');
      link.textContent = '読み込み中…';
      const before = count ? count.textContent : '';
      if (count) count.textContent = '読み込み中…';
      try {
        const firstAdded = appendPage(await fetchPage(link.href), link);
        // キーボード・スクリーンリーダーの利用者が続きから読めるよう、足した先頭のカードへフォーカスを移す
        // （スクロールはしない。画面は押した位置のまま、下に続きが伸びる）。カードが無ければ件数の行へ
        const target = firstAdded || count;
        if (target) target.focus({ preventScroll: true });
      } catch (err) {
        // 失敗しても、いままで読んだ分は消さない。もう一度押すと、次のページへふつうに遷移する。
        // 失敗は読み上げにも出す（件数の行は role="status"）
        link.dataset.failed = '1';
        link.removeAttribute('aria-disabled');
        link.textContent = '読み込めませんでした。次のページを開く';
        if (count) count.textContent = `読み込めませんでした（${before}）`;
      } finally {
        busy = false;
      }
    });

    // 離れる前にスクロール位置を覚える（戻ってきたとき、足し直したあとに同じ位置へ戻す）。
    // 足していないとき（pages = 0）も上書きして、再読み込み前の古い state が残らないようにする
    window.addEventListener('pagehide', () => {
      // 足し直しの途中で離れたときは、途中の scrollY ではなく、戻したい位置を残す
      writeState({ pages, y: restoreY === null ? window.scrollY : restoreY });
    });

    const saved = readState();
    const navEntry = window.performance && performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    const isBackForward = !!navEntry && navEntry.type === 'back_forward';
    if (saved && !isBackForward) {
      // 再読み込み・新しく開いたときは、前の足し具合を捨てる
      writeState(null);
    } else if (saved && saved.pages > 0 && isBackForward) {
      // 戻る・進むで帰ってきたとき: 足していたページ数ぶんを並べて取得し、順に足し直して、スクロール位置を戻す
      if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
      (async () => {
        busy = true;
        restoreY = saved.y || 0;
        try {
          const link = nav.querySelector('.load-more__next');
          if (link) {
            const first = Number(new URL(link.href, window.location.href).searchParams.get('page')) || 2;
            const hrefs = Array.from({ length: saved.pages }, (_, i) => withParam(link.href, 'page', first + i));
            // 並べて取る（一度に 4 ページまで。17 ページ分を一斉に取ると、回線とレート制限に厳しいため）。
            // 取れなかったページより先は足さない（順番がずれるため）。ボタンは足せたところの次を指すので、続きは押して足せる
            let ok = true;
            for (let i = 0; i < hrefs.length && ok; i += 4) {
              const chunk = await Promise.all(hrefs.slice(i, i + 4).map((h) => fetchPage(h).catch(() => null)));
              for (const page of chunk) {
                if (!page || !nav.contains(link)) {
                  ok = false;
                  break;
                }
                appendPage(page, link);
              }
            }
          }
        } catch (err) {
          // 足し直せたところまででよい
        } finally {
          busy = false;
          window.scrollTo(0, restoreY);
          restoreY = null;
        }
      })();
    }
  };

  const init = () => document.querySelectorAll('[data-load-more]').forEach(bind);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
